import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateJDFormDTO,
  UpdateJDFormDTO,
} from "@/modules/jd/domain/contracts";
import type {
  CreateJDLevelFormDTO,
  UpdateJDLevelFormDTO,
} from "@/modules/jd/domain/contracts/level";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { jdApi, jdLevelsApi } from "./client";

export const jdKeys = {
  all: ["jd"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["jd", "list", q] as const,
  detail: (id: string) => ["jd", "detail", id] as const,
  kpis: (id: string) => ["jd", "kpis", id] as const,
  versions: (id: string) => ["jd", "versions", id] as const,
};

export function useJDQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: jdKeys.list(q),
    queryFn: () => jdApi.list(q),
  });
}

export function useJDDetailQuery(id: string) {
  return useQuery({
    queryKey: jdKeys.detail(id),
    queryFn: () => jdApi.get(id),
    enabled: !!id,
  });
}

export function useJDByPositionId(positionId: string | undefined) {
  return useQuery({
    queryKey: ["jd", "by-position", positionId],
    queryFn: () => jdApi.getByPositionId(positionId!),
    enabled: !!positionId,
  });
}

export function useJdKpis(id: string) {
  return useQuery({
    queryKey: jdKeys.kpis(id),
    queryFn: () => jdApi.getKpis(id),
    enabled: !!id,
  });
}

export function useKpisByPositionId(positionId: string | undefined) {
  return useQuery({
    queryKey: ["jd", "position-kpis", positionId],
    queryFn: () => jdApi.getKpisByPositionId(positionId!),
    enabled: !!positionId,
  });
}

export function useAddJdKpi(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kpiId: string) => jdApi.addKpi(id, kpiId),
    onSuccess: () => {
      toast.success("ເພີ່ມ KPI ສຳເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.detail(id) });
      qc.invalidateQueries({ queryKey: jdKeys.kpis(id) });
    },
    onError: () => toast.error("ເພີ່ມ KPI ລົ້ມເຫຼວ"),
  });
}

export function useRemoveJdKpi(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kpiId: string) => jdApi.removeKpi(id, kpiId),
    onSuccess: () => {
      toast.success("ລຶບ KPI ອອກສຳເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.detail(id) });
      qc.invalidateQueries({ queryKey: jdKeys.kpis(id) });
    },
    onError: () => toast.error("ລຶບ KPI ລົ້ມເຫຼວ"),
  });
}

export function useCreateJD() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJDFormDTO) => jdApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ JD ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ສ້າງ JD ລົ້ມເຫຼວ"),
  });
}

export function useUpdateJD(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateJDFormDTO) => jdApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂ JD ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.detail(id) });
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂ JD ລົ້ມເຫຼວ"),
  });
}

export function usePublishJD() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => jdApi.publish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ເປີດໃຊ້ງານ JD ລົ້ມເຫຼວ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useUnpublishJD() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => jdApi.unpublish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ຍົກເລີກການເປີດໃຊ້ງານ JD ລົ້ມເຫຼວ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useArchiveJD() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => jdApi.archive(id),
    onSuccess: () => {
      toast.success("ປິດການໃຊ້ງານ JD ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ປິດການໃຊ້ງານ JD ລົ້ມເຫຼວ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useDuplicateJD() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (args: { id: string; title: string; departmentId: string }) =>
      jdApi.duplicate(args.id, {
        title: args.title,
        departmentId: args.departmentId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ຄັດລອກ JD ລົ້ມເຫຼວ"),
  });

  const run = (args: { id: string; title: string; departmentId: string }) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(args, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useDeleteJD() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => jdApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ລຶບ JD ລົ້ມເຫຼວ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useJDVersions(id: string) {
  return useQuery({
    queryKey: jdKeys.versions(id),
    queryFn: () => jdApi.getVersions(id),
    enabled: !!id,
  });
}

export function useJDVersion(id: string, versionId: string) {
  return useQuery({
    queryKey: ["jd", "version", id, versionId],
    queryFn: () => jdApi.getVersion(id, versionId),
    enabled: !!id && !!versionId,
  });
}

export function useSaveJDVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changeNote }: { id: string; changeNote?: string }) =>
      jdApi.saveVersion(id, changeNote),
    onSuccess: (_, { id }) => {
      toast.success("ບັນທຶກເວີຊັນສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.versions(id) });
      qc.invalidateQueries({ queryKey: jdKeys.detail(id) });
    },
    onError: () => toast.error("ບັນທຶກເວີຊັນລົ້ມເຫຼວ"),
  });
}

export function useRevertJDVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId: string }) =>
      jdApi.revertToVersion(id, versionId),
    onSuccess: (_, { id }) => {
      toast.success("ກັບຄືນເປັນເວີຊັນເກົ່າສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdKeys.detail(id) });
      qc.invalidateQueries({ queryKey: jdKeys.versions(id) });
      qc.invalidateQueries({ queryKey: jdKeys.all });
    },
    onError: () => toast.error("ກັບຄືນເວີຊັນລົ້ມເຫຼວ"),
  });
}

// JD Level hooks
export const jdLevelKeys = {
  all: ["jd-levels"] as const,
  lists: () => [...jdLevelKeys.all, "list"] as const,
  list: (query: OffsetPageQueryDTO) => [...jdLevelKeys.lists(), query] as const,
  details: () => [...jdLevelKeys.all, "detail"] as const,
  detail: (id: string) => [...jdLevelKeys.details(), id] as const,
};

export function useJDLevels(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: jdLevelKeys.list(query),
    queryFn: () => jdLevelsApi.list(query),
  });
}

export function useJDLevel(id: string) {
  return useQuery({
    queryKey: jdLevelKeys.detail(id),
    queryFn: () => jdLevelsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateJDLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJDLevelFormDTO) => jdLevelsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງລະດັບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdLevelKeys.all });
    },
    onError: () => toast.error("ສ້າງລະດັບລົ້ມເຫຼວ"),
  });
}

export function useUpdateJDLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateJDLevelFormDTO }) =>
      jdLevelsApi.update(id, input),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂລະດັບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdLevelKeys.detail(id) });
      qc.invalidateQueries({ queryKey: jdLevelKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂລະດັບລົ້ມເຫຼວ"),
  });
}

export function useDeleteJDLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => jdLevelsApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບລະດັບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: jdLevelKeys.all });
    },
    onError: () => toast.error("ລຶບລະດັບລົ້ມເຫຼວ"),
  });
}
